/**
 * Vocabulário das detecções — puro, sem React, para o check de contrato.
 *
 * Severidade, mecanismo e MITRE ATT&CK viram texto e classe de cor aqui, e só
 * aqui: a lista, a ficha, o panorama e a linha do tempo do processo leem a
 * MESMA tabela, então "alta" nunca é laranja numa tela e vermelha em outra.
 */
import type { Tone } from '../../components/primitives/Metric.tsx'
import type { AlertResponseAction, DetectionAlert, DetectionSeverity, MitreRef } from '../sse/types.ts'

/* ── severidade ──────────────────────────────────────────────────────────── */

export interface SeverityPresentation {
  key: DetectionSeverity | null
  /** Palavra escrita: a cor é o segundo canal, nunca o único (Badge.tsx). */
  label: string
  /** Três letras, para colunas estreitas e o trilho da lista. */
  code: string
  text: string
  border: string
  /** Fundo do trilho/marcador. */
  fill: string
  /** Tinta de fundo, discreta, para destacar uma linha. */
  tint: string
  /** 4 = crítica … 1 = baixa, 0 = desconhecida. Ordena a triagem. */
  rank: number
}

const SEVERITY: Record<DetectionSeverity, SeverityPresentation> = {
  critical: {
    key: 'critical',
    label: 'Crítica',
    code: 'CRT',
    text: 'text-crit',
    border: 'border-crit',
    fill: 'bg-crit',
    tint: 'bg-crit/8',
    rank: 4,
  },
  high: {
    key: 'high',
    label: 'Alta',
    code: 'ALT',
    text: 'text-high',
    border: 'border-high',
    fill: 'bg-high',
    tint: 'bg-high/8',
    rank: 3,
  },
  medium: {
    key: 'medium',
    label: 'Média',
    code: 'MED',
    text: 'text-warn',
    border: 'border-warn',
    fill: 'bg-warn',
    tint: 'bg-warn/8',
    rank: 2,
  },
  low: {
    key: 'low',
    label: 'Baixa',
    code: 'BXA',
    text: 'text-accent',
    border: 'border-accent',
    fill: 'bg-accent',
    tint: 'bg-accent/8',
    rank: 1,
  },
}

const UNKNOWN_SEVERITY: SeverityPresentation = {
  key: null,
  label: 'Sem severidade',
  code: '—',
  text: 'text-fg-muted',
  border: 'border-line-strong',
  fill: 'bg-line-strong',
  tint: '',
  rank: 0,
}

export function severityPresentation(severity: DetectionSeverity | null): SeverityPresentation {
  return severity === null ? UNKNOWN_SEVERITY : SEVERITY[severity]
}

/** Da mais grave para a mais branda — a ordem de leitura da triagem. */
export const SEVERITY_ORDER: readonly DetectionSeverity[] = ['critical', 'high', 'medium', 'low']

/* ── mecanismo ───────────────────────────────────────────────────────────── */

const MECHANISM: Record<string, { label: string; hint: string }> = {
  evento: { label: 'Evento', hint: 'Uma condição sobre um único evento.' },
  sequencia: {
    label: 'Sequência',
    hint: 'Eventos em ordem, ligados por uma chave, dentro de uma janela de tempo.',
  },
  limiar: { label: 'Limiar', hint: 'Eventos da mesma chave acima de um limite numa janela.' },
  correlacao: {
    label: 'Correlação',
    hint: 'Risco acumulado da entidade a partir de sinais de regras e de comportamento.',
  },
}

export function mechanismPresentation(mechanism: string | null): { label: string; hint: string } | null {
  if (mechanism === null) return null
  return MECHANISM[mechanism] ?? { label: mechanism, hint: 'Mecanismo informado pelo agente.' }
}

/* ── resposta do agente (data.response) ──────────────────────────────────── */

/**
 * As três leituras que a tela NÃO pode errar:
 *
 *  - `simulated` — modo simulação, NADA foi tocado. Neutro e tracejado: não pode
 *    parecer contenção, senão o analista fecha um alerta de máquina viva.
 *  - `failed` — a máquina CONTINUA comprometida. Vermelho, sempre.
 *  - `executed` — a ação aconteceu.
 *  - `recommended` — o agente NÃO isolou e recomenda: convite ao operador, não
 *    estado da máquina.
 *
 * Vocabulário aberto: ação ou desfecho desconhecido aparece COMO VEIO, em tom
 * neutro — nem promovido a contenção, nem escondido.
 */
export interface ResponseStatusPresentation {
  key: string | null
  label: string
  tone: Tone
  /** Tracejado: "não aconteceu de verdade". */
  dashed: boolean
  hint: string
  /** A ação aconteceu na máquina. Só `executed`. */
  contained: boolean
}

const RESPONSE_STATUS: Record<string, Omit<ResponseStatusPresentation, 'key'>> = {
  executed: {
    label: 'executada',
    tone: 'ok',
    dashed: false,
    hint: 'O agente executou a ação na máquina.',
    contained: true,
  },
  simulated: {
    label: 'simulada · nada tocado',
    tone: 'neutral',
    dashed: true,
    hint: 'Modo simulação: nada foi tocado.',
    contained: false,
  },
  skipped: {
    label: 'não aplicada',
    tone: 'neutral',
    dashed: false,
    hint: 'O agente decidiu não executar a ação.',
    contained: false,
  },
  failed: {
    label: 'falhou',
    tone: 'crit',
    dashed: false,
    hint: 'O agente tentou e FALHOU: a máquina continua comprometida.',
    contained: false,
  },
  // Regime "Só recomendar" (o padrão): o agente NÃO isolou e pede uma decisão.
  // Atenção, nem sucesso nem falha — e nunca um estado da máquina.
  recommended: {
    label: 'isolamento recomendado',
    tone: 'warn',
    dashed: false,
    hint: 'O agente não isolou: decida se isola.',
    contained: false,
  },
}

export function responseStatusPresentation(status: string | null): ResponseStatusPresentation {
  if (status === null) {
    return {
      key: null,
      label: 'desfecho não informado',
      tone: 'neutral',
      dashed: false,
      hint: 'O agente não informou o desfecho.',
      contained: false,
    }
  }
  const known = RESPONSE_STATUS[status]
  return known === undefined
    ? {
        key: status,
        label: status,
        tone: 'neutral',
        dashed: false,
        hint: `Desfecho "${status}" desconhecido.`,
        contained: false,
      }
    : { key: status, ...known }
}

const RESPONSE_ACTION: Record<string, string> = {
  kill_process: 'Encerrar processo',
  isolate_host: 'Isolar host',
  block_ip: 'Bloquear IP',
  // Web Defense: 429 por 60 s e challenge (o IP segue atendido, um operador decide).
  rate_limit_ip: 'Limitar IP',
  challenge_ip: 'Pôr em challenge',
}

export function responseActionLabel(action: string | null): string {
  if (action === null) return 'Ação não informada'
  return RESPONSE_ACTION[action] ?? action
}

/** Ações e desfechos conhecidos, para os seletores de filtro. */
export const RESPONSE_ACTIONS: readonly string[] = Object.keys(RESPONSE_ACTION)
export const RESPONSE_STATUSES: readonly string[] = Object.keys(RESPONSE_STATUS)

/**
 * Um selo por (ação, desfecho) distinto — o agente pode repetir a mesma ação, e
 * "encerrar processo · executada" duas vezes na linha é ruído. A ordem segue a
 * da resposta, com as falhas promovidas para a frente: é o que pede ação.
 */
export function responseBadges(response: readonly AlertResponseAction[]): AlertResponseAction[] {
  const seen = new Set<string>()
  const out: AlertResponseAction[] = []
  for (const item of response) {
    // `block_ip` de IPs diferentes são fatos diferentes: um selo por IP.
    const key = `${item.action ?? '?'}|${item.status ?? '?'}|${item.target?.ip ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out.sort(
    (a, b) => Number(b.status === 'failed') - Number(a.status === 'failed'),
  )
}

/** Atalhos da fila: recortes que a triagem usa todo dia. */
export const RESPONSE_SHORTCUTS: ReadonlyArray<{
  key: string
  label: string
  hint: string
  query: { responseAction?: string; responseStatus?: string; isolatedMachines?: boolean }
}> = [
  {
    key: 'failed',
    label: 'Contenção falhou',
    hint: 'Alertas em que alguma ação do agente FALHOU.',
    query: { responseStatus: 'failed' },
  },
  {
    key: 'isolated',
    label: 'Hosts isolados',
    hint: 'Alertas de máquinas que estão isoladas da rede AGORA.',
    query: { isolatedMachines: true },
  },
  {
    key: 'isolations',
    label: 'Isolamentos executados',
    hint: 'Alertas em que o agente isolou o host de fato (não simulado).',
    query: { responseAction: 'isolate_host', responseStatus: 'executed' },
  },
  {
    key: 'recommended',
    label: 'Isolamentos recomendados',
    hint: 'Alertas em que o agente recomendou isolar o host e NÃO isolou — à espera de decisão.',
    query: { responseAction: 'isolate_host', responseStatus: 'recommended' },
  },
  {
    key: 'ip_blocks',
    label: 'IPs bloqueados',
    hint: 'Alertas em que o agente bloqueou de fato o IP de quem varria portas (não simulado).',
    query: { responseAction: 'block_ip', responseStatus: 'executed' },
  },
]

/* ── MITRE ATT&CK ────────────────────────────────────────────────────────── */

/**
 * As táticas Enterprise, na ordem da cadeia de ataque. A matriz do panorama é
 * desenhada nesta ordem SEMPRE — inclusive as zeradas —, pelo mesmo motivo das
 * rejeições do console: a coluna de Persistência fica no mesmo lugar todo dia.
 */
export const TACTICS: ReadonlyArray<{ id: string; label: string; short: string }> = [
  { id: 'TA0043', label: 'Reconhecimento', short: 'Recon' },
  { id: 'TA0042', label: 'Preparação de recursos', short: 'Recursos' },
  { id: 'TA0001', label: 'Acesso inicial', short: 'Acesso' },
  { id: 'TA0002', label: 'Execução', short: 'Execução' },
  { id: 'TA0003', label: 'Persistência', short: 'Persist.' },
  { id: 'TA0004', label: 'Escalada de privilégio', short: 'Privilégio' },
  { id: 'TA0005', label: 'Evasão de defesa', short: 'Evasão' },
  { id: 'TA0006', label: 'Acesso a credenciais', short: 'Credenciais' },
  { id: 'TA0007', label: 'Descoberta', short: 'Descoberta' },
  { id: 'TA0008', label: 'Movimento lateral', short: 'Lateral' },
  { id: 'TA0009', label: 'Coleta', short: 'Coleta' },
  { id: 'TA0011', label: 'Comando e controle', short: 'C2' },
  { id: 'TA0010', label: 'Exfiltração', short: 'Exfiltração' },
  { id: 'TA0040', label: 'Impacto', short: 'Impacto' },
]

export function tacticLabel(ref: MitreRef): string {
  return TACTICS.find((t) => t.id === ref.id)?.label ?? ref.name ?? ref.id
}

/**
 * A tática dita como o cliente a diria: o que o atacante TENTOU fazer. A visão
 * geral do cliente fala esta língua; a fila e o panorama seguem no ATT&CK.
 */
const TACTIC_PLAIN: Record<string, string> = {
  TA0043: 'Mapear o seu ambiente',
  TA0042: 'Preparar a infraestrutura do ataque',
  TA0001: 'Entrar na rede',
  TA0002: 'Executar código malicioso',
  TA0003: 'Se manter na máquina após reiniciar',
  TA0004: 'Ganhar privilégios de administrador',
  TA0005: 'Driblar as defesas',
  TA0006: 'Roubar senhas e credenciais',
  TA0007: 'Explorar a rede e as máquinas',
  TA0008: 'Se espalhar para outras máquinas',
  TA0009: 'Juntar dados para levar',
  TA0011: 'Falar com um servidor do atacante',
  TA0010: 'Tirar dados da empresa',
  TA0040: 'Danificar ou sequestrar dados',
}

export function tacticPlainLabel(id: string, fallback: string): string {
  return TACTIC_PLAIN[id] ?? fallback
}

/** O que a ação de resposta FEZ, no particípio: "IP bloqueado", não "Bloquear IP". */
const RESPONSE_OUTCOME: Record<string, string> = {
  kill_process: 'Processo encerrado',
  isolate_host: 'Máquina isolada',
  block_ip: 'IP bloqueado',
  rate_limit_ip: 'IP limitado',
  challenge_ip: 'IP em challenge',
}

export function responseOutcomeLabel(action: string | null): string {
  return action === null ? 'Contido' : (RESPONSE_OUTCOME[action] ?? responseActionLabel(action))
}

/**
 * Link para a página pública do ATT&CK. Só ids no formato conhecido viram link:
 * o id vem do agente, e um texto arbitrário não pode virar URL.
 */
export function mitreUrl(id: string): string | null {
  if (/^TA\d{4}$/.test(id)) return `https://attack.mitre.org/tactics/${id}/`
  const technique = /^(T\d{4})(?:\.(\d{3}))?$/.exec(id)
  if (technique === null) return null
  return technique[2] === undefined
    ? `https://attack.mitre.org/techniques/${technique[1]}/`
    : `https://attack.mitre.org/techniques/${technique[1]}/${technique[2]}/`
}

/* ── panorama da janela carregada ────────────────────────────────────────── */

export interface Ranked {
  key: string
  label: string
  count: number
  /** A severidade mais alta vista nesta chave. */
  worst: DetectionSeverity | null
}

export interface DetectionSummary {
  total: number
  bySeverity: Record<DetectionSeverity, number>
  unknownSeverity: number
  rules: Ranked[]
  hosts: Ranked[]
  /** Contagem por tática, na ordem de `TACTICS`; táticas fora dela vão ao fim. */
  tactics: Array<{ id: string; label: string; short: string; count: number }>
  techniques: Ranked[]
  /** Carimbos de chegada (ms) do mais antigo e do mais novo. */
  span: { from: number; to: number } | null
}

function worse(a: DetectionSeverity | null, b: DetectionSeverity | null): DetectionSeverity | null {
  return severityPresentation(a).rank >= severityPresentation(b).rank ? a : b
}

function rank(map: Map<string, Ranked>, limit: number): Ranked[] {
  return [...map.values()]
    .sort(
      (a, b) =>
        b.count - a.count ||
        severityPresentation(b.worst).rank - severityPresentation(a.worst).rank ||
        a.label.localeCompare(b.label),
    )
    .slice(0, limit)
}

function bump(
  map: Map<string, Ranked>,
  key: string,
  label: string,
  severity: DetectionSeverity | null,
) {
  const current = map.get(key)
  if (current === undefined) map.set(key, { key, label, count: 1, worst: severity })
  else {
    current.count += 1
    current.worst = worse(current.worst, severity)
  }
}

/**
 * Agrega a janela CARREGADA. O servidor não conta alertas (nada de `count(*)` na
 * tabela de ingestão), então todo número do panorama é "nos N carregados" — e a
 * tela diz isso ao lado do número.
 */
export function summarize(alerts: readonly DetectionAlert[]): DetectionSummary {
  const bySeverity: Record<DetectionSeverity, number> = { critical: 0, high: 0, medium: 0, low: 0 }
  let unknownSeverity = 0
  const rules = new Map<string, Ranked>()
  const hosts = new Map<string, Ranked>()
  const techniques = new Map<string, Ranked>()
  const tacticCount = new Map<string, { label: string; count: number }>()
  let from = Number.POSITIVE_INFINITY
  let to = Number.NEGATIVE_INFINITY

  for (const alert of alerts) {
    if (alert.severity === null) unknownSeverity += 1
    else bySeverity[alert.severity] += 1

    const ruleKey = alert.rule_id ?? alert.row.native_id ?? '?'
    bump(rules, ruleKey, alert.rule_name ?? ruleKey, alert.severity)

    const host = alert.row.hostname ?? alert.host_hostname ?? alert.row.endpoint_id
    bump(hosts, alert.row.endpoint_id, host, alert.severity)

    for (const technique of alert.techniques) {
      bump(techniques, technique.id, technique.name ?? technique.id, alert.severity)
    }
    for (const tactic of alert.tactics) {
      const current = tacticCount.get(tactic.id)
      tacticCount.set(tactic.id, {
        label: tacticLabel(tactic),
        count: (current?.count ?? 0) + 1,
      })
    }

    const at = Date.parse(alert.row.received_at)
    if (Number.isFinite(at)) {
      from = Math.min(from, at)
      to = Math.max(to, at)
    }
  }

  const known = TACTICS.map((t) => ({ ...t, count: tacticCount.get(t.id)?.count ?? 0 }))
  const extra = [...tacticCount.entries()]
    .filter(([id]) => !TACTICS.some((t) => t.id === id))
    .map(([id, v]) => ({ id, label: v.label, short: v.label, count: v.count }))

  return {
    total: alerts.length,
    bySeverity,
    unknownSeverity,
    rules: rank(rules, 6),
    hosts: rank(hosts, 6),
    tactics: [...known, ...extra],
    techniques: rank(techniques, 8),
    span: Number.isFinite(from) ? { from, to } : null,
  }
}

/** Uma linha de evidência em texto: o objeto da ação, o que o analista lê primeiro. */
export function evidenceObject(e: {
  command_line: string | null
  file_path: string | null
  registry_path: string | null
  destination: string | null
  dns_question: string | null
}): string | null {
  return e.command_line ?? e.file_path ?? e.registry_path ?? e.destination ?? e.dns_question
}
